<template>
  <div class="fixed-top navbar-index">
    <div class="navbar navbar-main navbar-expand-md navbar-theme pl-0" :class="{ 'navbar--trial': authStore.showAnyBar }">
      <div class="navbar-brand d-flex align-items-center">
        <a @click.prevent="openSidebar" class="navbar-nav-link--hover sidebar-collapse--toggle mr-2">
          <sidebar-collapse-icon/>
        </a>
        <router-link :to="{ name: 'dashboard' }" class="d-inline-block" style="margin-top: 3px;">
          <brand-title class="brand-logo"/>
        </router-link>
      </div>
      <div class="navbar-collapse overflow-auto d-flex justify-content-center">
        <div class="d-flex w-100 navbar-menu">
          <div class="navbar-search--input">
            <search-input
              v-if="authStore.company"
              :show-search-button="false"
              v-model="navbarSearchInput"
              @keydown.enter="searchInvoices"
              :placeholder="$t('invoice.extra.navbar_search_placeholder')"
            />
          </div>
          <ul class="navbar-nav flex-row align-items-center" style="height: 36px">
            <li v-if="authStore.isAdmin" class="nav-item">
              <div class="badge badge-danger d-flex align-items-center">
                <span>Impersonating</span>
              </div>
            </li>

            <li v-if="authStore.isAdmin" class="nav-item">
              <a :href="logoutUrl()" class="navbar-nav-link pl-2 pr-0 pt-0 pb-0">
                <div
                  style="padding: 5px;"
                  v-tippy="{ arrow: true }"
                  :content="$t('actions.logout')"
                >
                  <door-icon/>
                </div>
              </a>
            </li>

            <li class="nav-item d-md-flex d-none" v-if="showPartnerships">
              <a class="navbar-nav-link p-0" href="#">
                <div
                  class="navbar-nav-link--hover d-flex p-1"
                  v-tippy="{ arrow: true, touch: false }"
                  :content="$t('extra.funding')"
                  @click.prevent="displaySmeGoBanner"
                >
                  <euro-icon class="icon--mini"/>
                </div>
              </a>
            </li>

            <li class="floating-wrapper nav-item" v-if="showDevelopmentMode">
              <dev-mode-select/>
            </li>

            <s-popper
              v-if="!authStore.isAdmin && authStore.company"
              placement="bottom"
              tag="li"
              class="nav-item d-flex mt-0"
            >
              <a class="navbar-nav-link dropdown-toggle p-0" href="#">
                <div
                  class="d-flex align-items-center py-1 px-1 navbar-nav-link--hover"
                  style="height: 28px;"
                  v-tippy="{ arrow: true, touch: false }"
                  :content="$t('actions.create')"
                >
                  <create-icon class="nav-icon"/>
                  <dropdown-icon class="ml-1"/>
                </div>
              </a>

              <template #popup>
                <div class="dropdown-menu navbar-create-dropdown p-0">
                  <span class="border-bottom d-block p-2 font-weight-midbold p-1">{{ $t('extra.new') }}</span>
                  <div class="nav-item nav-item-submenu border-bottom rounded-0 p-1">
                    <router-link
                      class="dropdown-item"
                      :to="{ name: 'invoice.create' }"
                      v-if="$auth.can('invoice.create')"
                      v-restrict="!$auth.canSub('invoices')"
                    >
                      <div>
                        <invoice-icon class="nav-icon"/>
                      </div>
                      <span class="font-weight-midbold pl-2">{{ $t('extra.invoice') }}</span>
                    </router-link>
                    <router-link
                      class="dropdown-item"
                      :to="{ name: 'expense.create' }"
                      v-if="$auth.can('expense.create') && $module.can('expenses')"
                      v-restrict
                    >
                      <div>
                        <expense-icon class="nav-icon"/>
                      </div>
                      <span class="font-weight-midbold pl-2">{{ $t('extra.expense') }}</span>
                    </router-link>
                    <router-link
                      class="dropdown-item"
                      :to="{ name: 'client.create' }"
                      v-if="$auth.can('client.create')"
                      v-restrict
                    >
                      <div>
                        <client-icon class="nav-icon"/>
                      </div>
                      <span class="font-weight-midbold pl-2">{{ $t('extra.client') }}</span>
                    </router-link>
                    <router-link
                      class="dropdown-item"
                      :to="{ name: 'product.create' }"
                      v-if="$auth.can('product.create')"
                      v-restrict
                    >
                      <div>
                        <products-icon class="nav-icon"/>
                      </div>
                      <span class="font-weight-midbold pl-2">{{ $t('extra.product') }}</span>
                    </router-link>
                  </div>
                  <div class="nav-item p-1 rounded-0">
                    <router-link
                      class="dropdown-item"
                      :to="{ name: 'commercial-suggestion.create' }"
                      v-if="$auth.can('commercial-suggestion.create') && $module.can('commercial-suggestion')"
                      v-restrict
                    >
                      <div class="sidebar-submenu--module_icon m-0">
                        <commercial-suggestion class="nav-icon"/>
                      </div>
                      <span class="font-weight-midbold pl-2">{{ $t('extra.commercial') }}</span>
                    </router-link>
                    <router-link
                      class="dropdown-item"
                      :to="{ name: 'expense-document-import.create' }"
                      v-if="$auth.can('expense-document-import.create') && $module.can('expenses')"
                      v-restrict
                    >
                      <div class="sidebar-submenu--module_icon m-0">
                        <expenses-logo class="nav-icon"/>
                      </div>
                      <span class="font-weight-midbold pl-2">{{ $t('expense-document-import.index.title') }}</span>
                    </router-link>
                    <router-link
                      class="dropdown-item"
                      :to="{ name: 'purchase-document-import.create' }"
                      v-if="$module.can('warehouse')"
                      v-restrict
                    >
                      <div class="sidebar-submenu--module_icon m-0">
                        <warehouse-logo class="nav-icon"/>
                      </div>
                      <span class="font-weight-midbold pl-2">{{ $t('purchase-document-import.index.title') }}</span>
                    </router-link>
                  </div>
                </div>
              </template>
            </s-popper>

            <hr class="border-right navbar-separator mx-1">

            <s-popper
              v-if="!authStore.isAdmin"
              placement="bottom"
              tag="li"
              class="nav-item d-flex"
            >
              <a class="navbar-nav-link dropdown-toggle p-0" href="#">
                <div
                  class="d-flex align-items-center p-1 navbar-nav-link--hover"
                  style="height: 28px;"
                  v-tippy="{ arrow: true, touch: false }"
                  :content="$t('actions.help')"
                >
                  <help-icon class="mr-0"/>
                  <dropdown-icon class="ml-1"/>
                </div>
              </a>

              <template #popup>
                <div class="dropdown-menu">
                  <div class="support-cards">
                    <a :href="'tel:' + $t('extra.support_phone')" class="support-card support-card--phone text-black" v-if="$feature.can('support-phone')">
                      <support-phone/>
                      <span class="text-black">{{ $t('extra.support_phone') }}</span>
                    </a>
                    <div class="support-card support-card--intercom cursor-pointer" @click="supportTab('chat')">
                      <support-chat/>
                      <span>{{ $t('extra.chat') }}</span>
                    </div>
                    <div class="support-card support-card--blog cursor-pointer" @click="supportTab('blog')">
                      <support-blog/>
                      <span style="max-width: 80px;" class="text-center">{{ $t('extra.blog') }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </s-popper>

            <s-popper
              v-if="!authStore.isAdmin"
              @closed="markNotificationsSeen"
              class="nav-item"
              placement="bottom-start"
              tag="li"
            >
              <a
                class="navbar-nav-link dropdown-toggle p-0"
                href="#"
                :class="[{ 'router-link-exact-active router-link-active': $route.name === 'notification.index' }]"
              >
                <div
                  style="padding: 5px;"
                  v-tippy="{ arrow: true, touch: false }"
                  :content="$t('notification.index.title')"
                  class="navbar-nav-link--hover"
                >
                  <notifications-icon/>
                  <span class="notification-icon-blue-badge text-white" v-if="authStore.hasUnreadNotification">
                    {{ authStore.unreadNotificationsLength }}
                  </span>
                </div>
              </a>

              <template #popup>
                <div class="dropdown-menu dropdown-content dropdown-notifications">
                  <div class="dropdown-scrollable mt-2 overflow-x-hidden" :class="{ 'pb-2': authStore.notifications.length > 0 }">
                    <ul class="list-inline">
                      <template v-if="authStore.notifications.length > 0">
                        <li class="mx-2" v-for="notification in authStore.notifications">
                          <div
                            :class="{ 'alert-primary': !notification.seen }"
                            class="d-flex align-items-center px-2 py-2 rounded cursor-default"
                          >
                            <div>
                              <span v-html="notification.title"/>
                              <div class="font-size-sm text-muted">{{ notification.diff }}</div>
                            </div>
                          </div>
                        </li>
                      </template>
                      <template v-else>
                        <li>
                          <span class="d-flex align-items-center px-2 py-2">{{ $t('notification.index.empty_message') }}</span>
                        </li>
                      </template>
                    </ul>
                  </div>

                  <router-link :to="{ name: 'notification.index' }" class="d-flex align-items-center px-2 py-2 font-weight-midbold">
                    {{ $t('notification.actions.view_all') }}
                    <redirect-icon class="ml-1"/>
                  </router-link>
                </div>
              </template>
            </s-popper>

            <li class="nav-item d-md-flex d-none">
              <router-link
                v-restrict-trial
                :to="{ name: 'module.index' }"
                class="navbar-nav-link p-0"
                :class="[{ 'router-link-exact-active': $route.name === 'module.edit' || $route.name === 'module.index' }]"
              >
                <div
                  style="padding: 5px;"
                  v-tippy="{ arrow: true, touch: false }"
                  :content="$t('module.title')"
                  class="navbar-nav-link--hover"
                >
                  <puzzle-icon/>
                </div>
              </router-link>
            </li>

            <s-popper
              v-if="authStore.user"
              placement="bottom-start"
              class="nav-item"
              tag="li"
              popup-class="navbar-extra--language-popup"
            >
              <a
                class="navbar-nav-link dropdown-toggle p-0"
                href="#"
              >
                <div
                  style="padding: 5px;"
                  v-tippy="{ arrow: true, touch: false }"
                  :content="$t('fields.language')"
                  class="navbar-nav-link--hover"
                >
                  <s-ui-block :value="loadingLangChange" class="navbar-extra--language_loader"/>
                  <component class="navbar-extra--country_icon" style="height: 16px; width: 16px" :is="getLanguageCountryIcon(authStore.user?.profile.language)"/>
                </div>
              </a>
              <template #popup>
                <div class="dropdown-menu">
                  <template v-for="(lang, index) in languagesCountries" :key="index">
                    <div v-if="lang.available" class="dropdown-item" @click="changeLanguageRequest(lang.id)">
                      <component class="mr-2" :is="getLanguageCountryIcon(lang.id)"/>
                      <span class="font-weight-midbold">{{ lang.title }}</span>
                    </div>
                  </template>
                </div>
              </template>
            </s-popper>

            <s-popper
              class="nav-item d-flex"
              placement="bottom-start"
              tag="li"
              v-if="authStore.user"
            >
              <a
                class="navbar-nav-link d-flex flex-row align-items-center dropdown-toggle p-0"
                href="#"
                :class="[{ 'router-link-exact-active router-link-active': $route.name === 'company.edit' }]"
              >
                <div
                  class="d-flex align-items-center py-1 px-1 navbar-nav-link--hover"
                  style="height: 28px;"
                >
                  <div class="position-relative">
                    <user-icon/>
                    <verified-icon v-if="authStore.user?.profile.smartid" class="user-verified-icon"/>
                  </div>

                  <div class="d-none d-md-flex flex-column font-weight-midbold ml-1">
                    <span>{{ authStore.fullName }}</span>
                  </div>

                  <dropdown-icon class="ml-2"/>
                </div>
              </a>

              <template #popup>
                <div class="dropdown-menu">
                  <router-link :to="{ name: 'company.edit', params: { tab: 'profile' } }" class="dropdown-item">
                    <i>
                      <user-circle-icon class="icon--small"/>
                    </i> {{ $t('profile.manage.title') }}
                  </router-link>
                  <a @click.prevent="openPaymentsModal" class="dropdown-item" v-if="authStore.company">
                    <i>
                      <circle-info-icon class="icon--small"/>
                    </i>
                    <div class="d-flex d-gap-10">
                      <span>{{ $t('subscription.title') }}</span>
                      <template v-if="isTrial">
                        <span class="badge badge-warning text-uppercase">Trial</span>
                      </template>
                      <template v-else-if="$user && $user.permissions.subscription.tag !== 'free'">
                        <span class="badge badge-primary text-uppercase">{{ $t('subscription.title_' + $user.permissions.subscription.tag) }}</span>
                      </template>
                      <span
                        v-if="subscriptionDaysLeft >= 0"
                        class="font-weight-midbold"
                        :class="subscriptionDaysLeft <= 7 ? 'text-danger' : ''"
                      >
                        {{ subscriptionDaysLeft }} d.
                      </span>
                    </div>
                  </a>
                  <router-link :to="{ name: 'company.edit', params: { tab: 'billing' } }" class="dropdown-item" v-if="authStore.company?.owner">
                    <i>
                      <card-icon class="icon--small"/>
                    </i> {{ $t('billing.title') }}
                  </router-link>
                  <router-link :to="{ name: 'company.edit', params: { tab: 'referrals' } }" class="dropdown-item" v-if="authStore.company">
                    <i>
                      <gift-icon class="icon--small"/>
                    </i>
                    <span>{{ $t('company.manage.referrals') }}</span>
                  </router-link>
                  <router-link v-restrict-trial :to="{ name: 'module.index' }" class="dropdown-item d-flex d-md-none">
                    <i>
                      <puzzle-icon style="width: 20px;"/>
                    </i>
                    <span>{{ $t('module.title') }}</span>
                  </router-link>
                  <a :href="logoutUrl()" class="dropdown-item">
                    <i>
                      <door-icon/>
                    </i> {{ $t('actions.logout') }}
                  </a>
                </div>
              </template>
            </s-popper>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <smego-banner v-if="showSmeGoBanner" v-model="showSmeGoBanner"/>
</template>

<script setup>
  import CreateIcon from '@/components/icons/create.vue';
  import DoorIcon from '@/components/icons/door.vue';
  import DropdownIcon from '@/components/icons/dropdown.vue';
  import HelpIcon from '@/components/icons/help.vue';
  import NotificationsIcon from '@/components/icons/notifications.vue';
  import UserIcon from '@/components/icons/user.vue';
  import { logoutUrl, userChangeLanguageUrl } from '@/api/auth';
  import { restrictTrial as vRestrictTrial, restrict as vRestrict } from '@/directives';
  import PuzzleIcon from '@/components/icons/puzzle.vue';
  import { computed, watch, ref, defineAsyncComponent, onMounted } from 'vue';
  import { useSidebarStore } from '@/stores/sidebar';
  import { useSubscriptionStore } from '@/stores/subscription';
  import { useAuthStore } from '@/stores/auth.ts';
  import SupportPhone from '@/components/icons/support-icons/support-phone.vue';
  import SupportChat from '@/components/icons/support-icons/support-chat.vue';
  import SupportBlog from '@/components/icons/support-icons/support-blog.vue';
  import { httpService, routerService } from '@/core';
  import { languagesCountries } from '@/data/country';
  import DevModeSelect from '@/layout/dashboard/partials/dev-mode-select.vue';
  import { openCrispChat } from '@/utils/crisp';
  import SearchInput from '@/pages/partials/search-input.vue';
  import SidebarCollapseIcon from '@/components/icons/sidebar-collapse-icon.vue';
  import InvoiceIcon from '@/components/icons/invoice.vue';
  import ExpenseIcon from '@/components/icons/expense.vue';
  import ClientIcon from '@/components/icons/clients.vue';
  import ProductsIcon from '@/components/icons/products.vue';
  import WarehouseLogo from '@/components/icons/modules/warehouse-logo.vue';
  import ExpensesLogo from '@/components/icons/modules/expenses-logo.vue';
  import CommercialSuggestion from '@/components/icons/modules/commercial-suggestion.vue';
  import VerifiedIcon from '@/components/icons/verified-icon.vue';
  import RedirectIcon from '@/components/icons/redirect.vue';
  import UserCircleIcon from '@/components/icons-v2/user-icon.vue';
  import GiftIcon from '@/components/icons-v2/gift-icon.vue';
  import BrandTitle from '@/pages/partials/brand-title.vue';
  import EuroIcon from '@/components/icons/euro.vue';
  import { amplitudeService } from '@/models/amplitude-service';
  import CardIcon from '@/components/icons-v2/card-icon.vue';
  import CircleInfoIcon from '@/components/icons-v2/circle-info-icon.vue';

  const SmegoBanner = defineAsyncComponent(() => import('@/modals/promo/smego-banner.vue'));

  const sidebarStore = useSidebarStore();
  const subscriptionStore = useSubscriptionStore();
  const authStore = useAuthStore();
  const closeSidebarIcon = ref(true);
  const loadingLangChange = ref(false);
  const navbarSearchInput = ref('');
  const showSmeGoBanner = ref(false);
  const showPartnerships = ref(false);

  const mobileOpen = computed(() => sidebarStore.mobileOpen);

  const isTrial = computed(() => authStore.user?.permissions?.subscription?.is_trial);

  const subscriptionDaysLeft = computed(() => authStore.user?.permissions?.subscription?.days_left);

  const showDevelopmentMode = computed(() => process.env.ENVIRONMENT_STATUS !== 'production');

  onMounted(() => {
    // Show SME Go banner only on non-mobile devices
    if (authStore.user?.profile?.show_partnerships && window.innerWidth > 1024) {
      showPartnerships.value = true;

      amplitudeService.trackEvent('[Frontend] Partnerships Link Displayed', {
        place: 'navbar-icon',
      });
    }
  });

  watch(mobileOpen, () => {
    closeSidebarIcon.value = sidebarStore.closeSidebarIcon;
  }, { immediate: true });

  function openSidebar() {
    sidebarStore.updateMobileOpen(sidebarStore.mobileOpen = !sidebarStore.mobileOpen);
  }

  async function markNotificationsSeen() {
    await authStore.markNotificationsSeen();
  }

  function openPaymentsModal() {
    subscriptionStore.setModalVisible(true);
  }

  function supportTab(type) {
    if (type === 'chat') {
      openCrispChat();
    } else if (type === 'blog') {
      if (authStore.user?.profile?.language === 'lt') {
        window.open('https://help.invoice123.com/lt', '_blank');
      } else if (authStore.user?.profile?.language === 'lv') {
        window.open('https://help.invoice123.com/lv', '_blank');
      } else {
        window.open('https://help.invoice123.com/en', '_blank');
      }
    }
  }

  function getLanguageCountryIcon(countryCode) {
    return defineAsyncComponent(() => import((`@/components/icons/flags/${countryCode}.vue`)));
  }

  function changeLanguageRequest(language) {
    if (isAbleToChangeLang(language)) {
      loadingLangChange.value = true;
      return httpService.patch(userChangeLanguageUrl(), {
        data: { language: language },
      })
        .then(() => {
          location.reload();
        })
        .finally(() => {
          loadingLangChange.value = false;
        });
    }

    return null;
  }

  function isAbleToChangeLang(language) {
    if (authStore.isAdmin) {
      return true;
    }

    return language !== authStore.user?.profile.language;
  }

  function searchInvoices() {
    routerService.push({ name: 'invoice.index', query: { text: navbarSearchInput.value } });
  }

  function displaySmeGoBanner() {
    showSmeGoBanner.value = true;

    amplitudeService.trackEvent('[Frontend] Partnerships Link Clicked', {
      place: 'navbar-icon',
    });

    amplitudeService.trackEvent('[Frontend] Partnerships Viewed', {
      partnerships: ['SmeGo', 'Test'],
    });
  }
</script>
